import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Trophy, Crown, Star, Heart, Medal, Zap, Users, 
  Gamepad2, Shield, TrendingUp, Loader2, Lock
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

const BADGE_ICONS = {
  trophy: Trophy,
  crown: Crown,
  star: Star,
  heart: Heart,
  medal: Medal,
  zap: Zap,
  users: Users,
  gamepad2: Gamepad2,
  shield: Shield,
  "trending-up": TrendingUp,
};

export default function Badges() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allBadges, setAllBadges] = useState([]);
  const [userBadges, setUserBadges] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const badgesRes = await api.getAllBadges();
        setAllBadges(badgesRes.data);
        
        if (user) {
          const userBadgesRes = await api.getUserBadges(user.user_id);
          setUserBadges(userBadgesRes.data.badges);
        }
      } catch (error) {
        console.error("Error fetching badges:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const earnedBadgeIds = new Set(userBadges.map(b => b.badge_id));
  
  const categories = {
    achievement: { name: "Başarı Rozetleri", icon: Trophy },
    season: { name: "Sezon Rozetleri", icon: Crown },
    tournament: { name: "Turnuva Rozetleri", icon: Medal },
    social: { name: "Sosyal Rozetler", icon: Heart },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="badges-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Rozetler
        </h1>
        <p className="text-[#B1C1C1] mt-1">
          {user ? `${userBadges.length}/${allBadges.length} rozet kazandın` : "Tüm rozetleri keşfet"}
        </p>
      </div>

      {/* User's Earned Badges */}
      {user && userBadges.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
            Kazandığın Rozetler
          </h2>
          <div className="flex flex-wrap gap-4">
            {userBadges.map((badge) => {
              const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
              return (
                <div
                  key={badge.badge_id}
                  className="glass-card p-4 flex items-center gap-4 animate-fade-in-up"
                  style={{ borderColor: `${badge.color}50` }}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${badge.color}33` }}
                  >
                    <IconComponent className="w-7 h-7" style={{ color: badge.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{badge.name_tr}</h3>
                    <p className="text-xs text-[#64748B]">{badge.description}</p>
                    <p className="text-xs text-[#4DFFFF] mt-1">
                      {new Date(badge.earned_at).toLocaleDateString("tr-TR")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Badges by Category */}
      {Object.entries(categories).map(([categoryId, categoryInfo]) => {
        const categoryBadges = allBadges.filter(b => b.category === categoryId);
        if (categoryBadges.length === 0) return null;
        
        const CategoryIcon = categoryInfo.icon;
        
        return (
          <div key={categoryId} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <CategoryIcon className="w-5 h-5 text-[#C6A87C]" />
              <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                {categoryInfo.name}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryBadges.map((badge) => {
                const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                const isEarned = earnedBadgeIds.has(badge.badge_id);
                
                return (
                  <div
                    key={badge.badge_id}
                    className={`glass-card p-4 transition-all ${
                      isEarned 
                        ? "border-l-4" 
                        : "opacity-60 grayscale"
                    }`}
                    style={{ borderLeftColor: isEarned ? badge.color : 'transparent' }}
                    data-testid={`badge-${badge.badge_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isEarned ? "" : "bg-white/5"
                        }`}
                        style={{ backgroundColor: isEarned ? `${badge.color}33` : undefined }}
                      >
                        {isEarned ? (
                          <IconComponent className="w-6 h-6" style={{ color: badge.color }} />
                        ) : (
                          <Lock className="w-5 h-5 text-[#64748B]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold ${isEarned ? "text-white" : "text-[#64748B]"}`}>
                          {badge.name_tr}
                        </h3>
                        <p className="text-xs text-[#64748B]">{badge.description}</p>
                      </div>
                      {isEarned && (
                        <div className="w-6 h-6 rounded-full bg-[#059669]/20 flex items-center justify-center">
                          <svg className="w-4 h-4 text-[#059669]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* How to Earn */}
      <div className="glass-card p-6 mt-8">
        <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
          Rozetler Nasıl Kazanılır?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <Trophy className="w-5 h-5 text-[#C6A87C] mt-0.5" />
            <div>
              <p className="text-white font-medium">Başarı Rozetleri</p>
              <p className="text-[#64748B]">Oyun oynayarak, ligler kurarak ve galibiyetler kazanarak</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-[#FFD700] mt-0.5" />
            <div>
              <p className="text-white font-medium">Sezon Rozetleri</p>
              <p className="text-[#64748B]">Bir sezonda en çok galibiyet alarak (MVP)</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Medal className="w-5 h-5 text-[#9333EA] mt-0.5" />
            <div>
              <p className="text-white font-medium">Turnuva Rozetleri</p>
              <p className="text-[#64748B]">Turnuvalarda şampiyon olarak</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-[#F61225] mt-0.5" />
            <div>
              <p className="text-white font-medium">Sosyal Rozetler</p>
              <p className="text-[#64748B]">Takipçi kazanarak ve Fair Play oyları alarak</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
